import { useFormikContext } from 'formik';
import { Dropdown, ErrorContainer, InputField, LabelComponent, Stack } from 'components';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import {
  whoDidYouReachData,
  howDidYouReachThemData as _howDidYouReachThemData,
  whatTypeOfPlaceWereYouInData as _whatTypeOfPlaceWereYouInData,
  whoWereYouTryingToReachData,
  whatTypeOfPlaceWereYouInViewOnlyData,
  howDidYouReachThemViewOnlyData
} from 'store/clients/clientsSlice';
import { useParams } from 'react-router-dom';
import { useEffect } from 'react';
import { getSites } from 'store/clientProfile/clientProfileActions';

import Branch from '../../components/branch';

interface WhoDidYouReachProps {
  disabled: boolean;
}

const whatTypeOfPlaceWereYouInData = _whatTypeOfPlaceWereYouInData.filter(
  (p) => !whatTypeOfPlaceWereYouInViewOnlyData.some((v) => v.id === p.id)
);

const howDidYouReachThemData = _howDidYouReachThemData.filter(
  (p) => !howDidYouReachThemViewOnlyData.some((v) => v.id === p.id)
);

export const WhoWereYouTryingToReachResetFields = [
  { key: 'whoWereYouTryingToReach', value: '' },
  { key: 'howDidYouTryThem', value: '' },
  { key: 'whatTypeOfPlaceWereYouIn', value: '' }
];

const WhoWereYouTryingToReach = ({ disabled }: { disabled: boolean }) => {
  const formik = useFormikContext<{
    whoWereYouTryingToReach: string;
    howDidYouTryThem: string;
    whatTypeOfPlaceWereYouIn: string;
  }>();

  const showHowDidYouTryThem = (whoWereYouTryingToReach: string) =>
    whoWereYouTryingToReach?.length > 0 &&
    !whoWereYouTryingToReach?.includes('WORK_ON_MY_CLIENTS_BEHALF');

  const showWhatTypeOfPlaceWereYouIn = (howDidYouTryThem: string) =>
    howDidYouTryThem?.length > 0 && howDidYouTryThem?.includes('IN_PERSON');

  return (
    <Stack>
      <Branch label={<LabelComponent mandatory text="Who were you trying to reach?" />}>
        <Dropdown
          value={formik.values.whoWereYouTryingToReach ?? []}
          withGradient
          label="Select"
          name="whoWereYouTryingToReach"
          data={whoWereYouTryingToReachData}
          onChange={(e) => {
            formik.handleChange(e);
            if (!showHowDidYouTryThem(e.target.value as string)) {
              formik.setFieldValue('howDidYouTryThem', '');
              formik.setFieldTouched('howDidYouTryThem', false);
              formik.setFieldValue('whatTypeOfPlaceWereYouIn', '');
              formik.setFieldTouched('whatTypeOfPlaceWereYouIn', false);
            }
          }}
          onBlur={formik.handleBlur}
          disabled={disabled}
        />
        <ErrorContainer
          visible={
            formik.touched.whoWereYouTryingToReach &&
            (formik.errors.whoWereYouTryingToReach as string)
          }
          errors={formik.errors}
          name="whoWereYouTryingToReach"
        />
        {showHowDidYouTryThem(formik.values.whoWereYouTryingToReach) && (
          <Branch label={<LabelComponent mandatory text="How did you try them?" />}>
            <Dropdown
              value={formik.values.howDidYouTryThem}
              withGradient
              label="Select"
              name="howDidYouTryThem"
              data={howDidYouReachThemData}
              onChange={(e) => {
                formik.handleChange(e);
                if (!showWhatTypeOfPlaceWereYouIn(e.target.value as string)) {
                  formik.setFieldValue('whatTypeOfPlaceWereYouIn', '');
                  formik.setFieldTouched('whatTypeOfPlaceWereYouIn', false);
                }
              }}
              onBlur={formik.handleBlur}
              disabled={disabled}
            />
            <ErrorContainer
              visible={
                formik.touched.howDidYouTryThem && (formik.errors.howDidYouTryThem as string)
              }
              errors={formik.errors}
              name="howDidYouTryThem"
            />
            {showWhatTypeOfPlaceWereYouIn(formik.values.howDidYouTryThem) && (
              <Branch label={<LabelComponent mandatory text="What type of place were you in?" />}>
                <Dropdown
                  value={formik.values.whatTypeOfPlaceWereYouIn}
                  withGradient
                  label="Select"
                  name="whatTypeOfPlaceWereYouIn"
                  data={whatTypeOfPlaceWereYouInData}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  disabled={disabled}
                />
                <ErrorContainer
                  visible={
                    formik.touched.whatTypeOfPlaceWereYouIn &&
                    formik.errors.whatTypeOfPlaceWereYouIn
                  }
                  errors={formik.errors}
                  name="whatTypeOfPlaceWereYouIn"
                />
              </Branch>
            )}
          </Branch>
        )}
      </Branch>
    </Stack>
  );
};

export const WhichProviderResetFields = [
  { key: 'billingProviderId', value: '' },
  { key: 'otherProviderName', value: '' }
];

const WhichProvider = ({ disabled }: { disabled: boolean }) => {
  const formik = useFormikContext<{
    billingProviderId: string;
    otherProviderName: string;
  }>();
  const providers = useAppSelector((state) => state.clients.billingProviders.data);

  const showProviderName = (billingProviderId: string) => {
    return billingProviderId === '-1';
  };

  return (
    <Branch label={<LabelComponent mandatory text="Which provider?" />}>
      <Dropdown
        value={formik.values.billingProviderId ?? []}
        withGradient
        label="Select"
        name="billingProviderId"
        data={providers}
        onChange={(e) => {
          formik.handleChange(e);
          if (!showProviderName(e.target.value as string)) {
            formik.setFieldValue('otherProviderName', '');
            formik.setFieldTouched('otherProviderName', false);
          }
        }}
        onBlur={formik.handleBlur}
        disabled={disabled}
      />
      <ErrorContainer
        visible={formik.touched.billingProviderId && (formik.errors.billingProviderId as string)}
        errors={formik.errors}
        name="billingProviderId"
      />
      {showProviderName(formik.values.billingProviderId) && (
        <Branch label={<LabelComponent mandatory text="Provider Name" />}>
          <InputField
            value={formik.values.otherProviderName}
            withGradient
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            name="otherProviderName"
            placeholder="Type here"
          />
          <ErrorContainer
            visible={
              formik.touched.otherProviderName && (formik.errors.otherProviderName as string)
            }
            errors={formik.errors}
            name="otherProviderName"
          />
        </Branch>
      )}
    </Branch>
  );
};

export const HowDidYouReachThemResetFields = [
  { key: 'howDidYouReachThem', value: '' },
  { key: 'whatTypeOfPlaceWereYouIn', value: '' },
  { key: 'siteId', value: null }
];

const HowDidYouReachThem = ({ disabled }: { disabled: boolean }) => {
  const dispatch = useAppDispatch();
  const { clientId } = useParams();
  const { sites } = useAppSelector((state) => state.clientProfile);
  const sitesEnabled = useAppSelector((state) => state.clients.client.data?.enableProgramSites);

  const formik = useFormikContext<{
    howDidYouReachThem: string;
    whatTypeOfPlaceWereYouIn: string;
    siteId: number;
  }>();

  const showWhatTypeOfPlaceWereYouIn = (howDidYouReachThem: string) =>
    howDidYouReachThem?.length > 0 && howDidYouReachThem?.includes('IN_PERSON');

  const showSites = (whatTypeOfPlaceWereYouIn: string) =>
    sitesEnabled &&
    whatTypeOfPlaceWereYouIn !== '' &&
    whatTypeOfPlaceWereYouIn !== null &&
    ['EMERGENCY_DEPARTMENT', 'HOSPITAL', 'MEDICAL_OFFICE', 'OTHER_IN_THE_COMMUNITY'].includes(
      whatTypeOfPlaceWereYouIn
    );

  useEffect(() => {
    if (clientId) {
      dispatch(getSites({ clientId }));
    }
  }, [clientId]);

  return (
    <Branch label={<LabelComponent mandatory text="How did you reach them?" />}>
      <Dropdown
        value={formik.values.howDidYouReachThem}
        withGradient
        label="Select"
        name="howDidYouReachThem"
        data={howDidYouReachThemData}
        onChange={(e) => {
          formik.handleChange(e);
          if (!showWhatTypeOfPlaceWereYouIn(e.target.value as string)) {
            formik.setFieldValue('whatTypeOfPlaceWereYouIn', '');
            formik.setFieldTouched('whatTypeOfPlaceWereYouIn', false);
            formik.setFieldValue('siteId', null);
            formik.setFieldTouched('siteId', false);
          }
        }}
        onBlur={formik.handleBlur}
        disabled={disabled}
      />
      <ErrorContainer
        visible={formik.touched.howDidYouReachThem && (formik.errors.howDidYouReachThem as string)}
        errors={formik.errors}
        name="howDidYouReachThem"
      />
      {showWhatTypeOfPlaceWereYouIn(formik.values.howDidYouReachThem) && (
        <Branch label={<LabelComponent mandatory text="What type of place were you in?" />}>
          <Stack mb="4px">
            <Dropdown
              value={formik.values.whatTypeOfPlaceWereYouIn}
              withGradient
              label="Select"
              name="whatTypeOfPlaceWereYouIn"
              data={whatTypeOfPlaceWereYouInData}
              onChange={(e) => {
                formik.handleChange(e);
                if (!showSites(e.target.value as string)) {
                  formik.setFieldValue('siteId', null);
                  formik.setFieldTouched('siteId', false);
                }
              }}
              onBlur={formik.handleBlur}
              disabled={disabled}
            />
            <ErrorContainer
              visible={
                formik.touched.whatTypeOfPlaceWereYouIn && formik.errors.whatTypeOfPlaceWereYouIn
              }
              errors={formik.errors}
              name="whatTypeOfPlaceWereYouIn"
            />
          </Stack>
          {showSites(formik.values.whatTypeOfPlaceWereYouIn) && (
            <Branch label={<LabelComponent mandatory text="What specific site?" />}>
              <Dropdown
                value={formik.values.siteId?.toString() ?? ''}
                smallDropdown
                withGradient
                label="Select"
                data={sites.data?.map((site) => ({
                  id: site.id,
                  name: site.name,
                  category: site.active ? 'ACTIVE' : 'INACTIVE'
                }))}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                name="siteId"
                disabled={disabled}
                categoryList={[
                  { id: 'ACTIVE', label: 'Active' },
                  { id: 'INACTIVE', label: 'Inactive' }
                ]}
              />
              <ErrorContainer
                visible={formik.touched.siteId && formik.errors.siteId}
                errors={formik.errors}
                name="siteId"
              />
            </Branch>
          )}
        </Branch>
      )}
    </Branch>
  );
};

export const WhoDidYouReach = ({ disabled }: WhoDidYouReachProps) => {
  const formik = useFormikContext<{
    whoDidYouReach: string[];
    howDidYouReachThem: string;
    whichProvider: string[];
    otherProviderName: string;
    whatTypeOfPlaceWereYouIn: string;
    siteId: number;
    whoWereYouTryingToReach: string;
    howDidYouTryThem: string;
  }>();

  const showWhoWereYouTryingToReach = (whoDidYouReach: string[]) =>
    whoDidYouReach?.length > 0 && whoDidYouReach?.includes('NO_ONE');

  const showWhichProvider = (whoDidYouReach: string[]) =>
    whoDidYouReach?.length > 0 && whoDidYouReach?.includes('PROVIDER');

  const showHowDidYouReachThem = (whoDidYouReach: string[]) =>
    whoDidYouReach?.length > 0 && !whoDidYouReach?.includes('NO_ONE');

  return (
    <Stack>
      <LabelComponent mandatory text="Who did you reach?" />
      <Dropdown
        withGradient
        label="Select"
        data={whoDidYouReachData}
        onBlur={formik.handleBlur}
        onChange={(e) => {
          if (
            !formik.values.whoDidYouReach.includes('NO_ONE') &&
            e.target.value.includes('NO_ONE')
          ) {
            formik.handleChange({ ...e, target: { ...e.target, value: ['NO_ONE'] } });

            if (!showWhoWereYouTryingToReach(e.target.value as string[])) {
              WhoWereYouTryingToReachResetFields.forEach((field) => {
                formik.setFieldValue(field.key, field.value);
                formik.setFieldTouched(field.key, false);
              });
            }

            if (!showWhichProvider(e.target.value as string[])) {
              WhichProviderResetFields.forEach((field) => {
                formik.setFieldValue(field.key, field.value);
                formik.setFieldTouched(field.key, false);
              });
            }

            if (!showHowDidYouReachThem(e.target.value as string[])) {
              console.log('HowDidYouReachThemResetFields');
              HowDidYouReachThemResetFields.forEach((field) => {
                formik.setFieldValue(field.key, field.value);
                formik.setFieldTouched(field.key, false);
              });
            }

            return;
          } else if (formik.values.whoDidYouReach.includes('NO_ONE')) {
            const valuesWithoutNOOne = (e.target.value as string[]).filter(
              (value) => value !== 'NO_ONE'
            );
            formik.handleChange({ ...e, target: { ...e.target, value: valuesWithoutNOOne } });

            if (!showWhoWereYouTryingToReach(e.target.value as string[])) {
              WhoWereYouTryingToReachResetFields.forEach((field) => {
                formik.setFieldValue(field.key, field.value);
                formik.setFieldTouched(field.key, false);
              });
            }

            if (!showWhichProvider(e.target.value as string[])) {
              WhichProviderResetFields.forEach((field) => {
                formik.setFieldValue(field.key, field.value);
                formik.setFieldTouched(field.key, false);
              });
            }

            if (!showHowDidYouReachThem(e.target.value as string[])) {
              HowDidYouReachThemResetFields.forEach((field) => {
                formik.setFieldValue(field.key, field.value);
                formik.setFieldTouched(field.key, false);
              });
            }

            return;
          }

          if (!showWhoWereYouTryingToReach(e.target.value as string[])) {
            WhoWereYouTryingToReachResetFields.forEach((field) => {
              formik.setFieldValue(field.key, field.value);
              formik.setFieldTouched(field.key, false);
            });
          }

          if (!showWhichProvider(e.target.value as string[])) {
            WhichProviderResetFields.forEach((field) => {
              formik.setFieldValue(field.key, field.value);
              formik.setFieldTouched(field.key, false);
            });
          }

          if (!showHowDidYouReachThem(e.target.value as string[])) {
            console.log('HowDidYouReachThemResetFields');
            HowDidYouReachThemResetFields.forEach((field) => {
              formik.setFieldValue(field.key, field.value);
              formik.setFieldTouched(field.key, false);
            });
          }

          formik.handleChange(e);
        }}
        name="whoDidYouReach"
        id="whoDidYouReach"
        value={formik.values.whoDidYouReach ?? ''}
        disabled={disabled}
        multiSelect
      />
      <ErrorContainer
        visible={formik.touched.whoDidYouReach && (formik.errors.whoDidYouReach as string)}
        errors={formik.errors}
        name="whoDidYouReach"
      />
      <Stack>
        {showWhoWereYouTryingToReach(formik.values.whoDidYouReach) && (
          <WhoWereYouTryingToReach disabled={disabled} />
        )}
        {showWhichProvider(formik.values.whoDidYouReach) && <WhichProvider disabled={disabled} />}
        {showHowDidYouReachThem(formik.values.whoDidYouReach) && (
          <HowDidYouReachThem disabled={disabled} />
        )}
      </Stack>
    </Stack>
  );
};
